import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ApiComment,
  ApiCommentPosition,
  CreateCommentInput,
} from "../lib/api";
import { readReviewer } from "../lib/reviewerIdentity";
import { CommentCard, Composer, type OwnerActions } from "./CommentParts";

// Floating popover anchored to a pin / click point inside the iframe.
//
// Two modes:
//   - "new"    → just the composer
//   - "thread" → root comment + replies + reply composer + actions
//
// Position is in the parent's viewport coordinate space. The caller
// converts iframe-internal coords to parent coords by adding the
// iframe's getBoundingClientRect().left/top before passing them in.
//
// The popover places itself to the right of the anchor by default and
// flips to the left when it would overflow the viewport. We also clamp
// the vertical position so the body always fits on screen.

export type PinPopoverState =
  | {
      kind: "new";
      anchor: { x: number; y: number };
      position: ApiCommentPosition;
      pageUrl: string;
    }
  | {
      kind: "thread";
      anchor: { x: number; y: number };
      rootId: string;
    };

type Props = {
  state: PinPopoverState;
  onClose: () => void;

  // Thread mode needs the full comment list for the page to assemble
  // root + replies; new mode ignores this.
  comments: ApiComment[];

  defaultIdentity: { name: string; email: string } | null;
  onCreate: (input: CreateCommentInput) => Promise<ApiComment>;
  onEdit: (commentId: string, body: string) => Promise<void>;
  ownerActions: OwnerActions | null;
  onFocusPin: (commentId: string) => void;
};

const POPOVER_WIDTH = 320;
const ANCHOR_GAP = 12;

export function PinPopover({
  state,
  onClose,
  comments,
  defaultIdentity,
  onCreate,
  onEdit,
  ownerActions,
  onFocusPin,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [replying, setReplying] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Close on Escape; click-outside is handled per pointer event below
  // (capturing on the window so iframe clicks don't bubble here).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const node = ref.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      onClose();
    }
    // Use mousedown so the click that *opens* this popover (handled via
    // postMessage from the iframe) doesn't immediately trigger the
    // outside-close on its own mouseup.
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [onClose]);

  // Reset transient sub-state when the popover anchors to a new pin.
  useEffect(() => {
    setReplying(false);
    setEditingId(null);
  }, [state.kind === "thread" ? state.rootId : state.kind]);

  const placement = useMemo(
    () => computePlacement(state.anchor),
    [state.anchor.x, state.anchor.y],
  );

  const currentUserEmail = (
    defaultIdentity?.email ?? readReviewer()?.email ?? ""
  ).toLowerCase();

  let body: React.ReactNode = null;
  if (state.kind === "new") {
    body = (
      <Composer
        pageUrl={state.pageUrl}
        position={state.position}
        defaultIdentity={defaultIdentity}
        onCreate={async (input) => {
          const created = await onCreate(input);
          // onCreate clears pendingPosition upstream which makes the
          // parent unmount us — no explicit close needed here.
          return created;
        }}
        onCancel={onClose}
        kind="root"
        variant="popover"
      />
    );
  } else {
    const root = comments.find((c) => c.id === state.rootId);
    if (!root) {
      // Pin's comment was deleted between click and render — just close.
      // Effect below schedules the close so we don't setState during render.
      body = (
        <DeferredClose onClose={onClose}>
          <div className="p-4 text-xs text-neutral-500">
            That comment is no longer available.
          </div>
        </DeferredClose>
      );
    } else {
      const replies = comments
        .filter((c) => c.parentId === root.id)
        .sort((a, b) => a.createdAt - b.createdAt);
      const rootEditable =
        !!currentUserEmail && root.authorEmail.toLowerCase() === currentUserEmail;
      body = (
        <div className="max-h-[420px] overflow-y-auto p-4">
          <CommentCard
            comment={root}
            editing={editingId === root.id}
            editable={rootEditable}
            onStartEdit={() => setEditingId(root.id)}
            onCancelEdit={() => setEditingId(null)}
            onSaveEdit={async (next) => {
              await onEdit(root.id, next);
              setEditingId(null);
            }}
            onResolve={ownerActions?.onResolve}
            onReopen={ownerActions?.onReopen}
            onDelete={ownerActions?.onDelete}
            onReply={() => setReplying(true)}
            onFocusPin={() => onFocusPin(root.id)}
          />
          {replies.length > 0 ? (
            <ul className="mt-3 ml-4 space-y-3 border-l border-neutral-200 pl-3">
              {replies.map((r) => {
                const replyEditable =
                  !!currentUserEmail && r.authorEmail.toLowerCase() === currentUserEmail;
                return (
                  <li key={r.id}>
                    <CommentCard
                      comment={r}
                      compact
                      editing={editingId === r.id}
                      editable={replyEditable}
                      onStartEdit={() => setEditingId(r.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveEdit={async (next) => {
                        await onEdit(r.id, next);
                        setEditingId(null);
                      }}
                      onDelete={ownerActions?.onDelete}
                    />
                  </li>
                );
              })}
            </ul>
          ) : null}
          {replying ? (
            <div className="mt-3 ml-4 border-l border-neutral-200 pl-3">
              <Composer
                pageUrl={(root && (root as ApiComment).pageUrl) || ""}
                position={root.position}
                parentId={root.id}
                defaultIdentity={defaultIdentity}
                onCreate={onCreate}
                onCancel={() => setReplying(false)}
                kind="reply"
                variant="popover"
              />
            </div>
          ) : null}
        </div>
      );
    }
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={state.kind === "new" ? "New comment" : "Comment thread"}
      style={{
        position: "fixed",
        left: placement.left,
        top: placement.top,
        width: POPOVER_WIDTH,
        zIndex: 100,
      }}
      // Background prevents the iframe behind from showing through; the
      // ring + shadow lift it visually like InVision's pin popover.
      className="rounded-lg border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5"
    >
      {body}
    </div>
  );
}

function computePlacement(anchor: { x: number; y: number }): {
  left: number;
  top: number;
} {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer to the right of the anchor; flip left if it'd overflow.
  let left = anchor.x + ANCHOR_GAP;
  if (left + POPOVER_WIDTH > vw - 12) {
    left = Math.max(12, anchor.x - POPOVER_WIDTH - ANCHOR_GAP);
  }
  // Vertical: top of popover at the pin's anchor Y, but clamp so the
  // body has room to render.
  let top = anchor.y + ANCHOR_GAP;
  const ASSUMED_HEIGHT = 320; // worst-case for clamp
  if (top + ASSUMED_HEIGHT > vh - 12) {
    top = Math.max(12, vh - ASSUMED_HEIGHT - 12);
  }
  return { left, top };
}

// Wrapper that fires `onClose` on the next tick so we don't update state
// during a render of the parent.
function DeferredClose({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const t = setTimeout(onClose, 1500);
    return () => clearTimeout(t);
  }, [onClose]);
  return <>{children}</>;
}
